/**
 * SEO Component
 * Manages meta tags, Open Graph, Twitter Cards, and structured data for each route
 */

import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import type { SEOProps } from '@/shared/types/seo.types';
import {
  buildCanonicalUrl,
  buildFullTitle,
  buildOGImageUrl,
  getRouteConfig,
  seoConfig,
} from '@/shared/config/seo.config.ts';
import { StructuredDataComponent } from './StructuredData';

export function SEO(props: SEOProps = {}): React.ReactElement {
  const location = useLocation();
  const routeConfig = getRouteConfig(location.pathname);

  // Merge route config with component props (props take precedence)
  const {
    title = routeConfig?.title,
    description = routeConfig?.description || seoConfig.siteDescription,
    keywords = routeConfig?.keywords || [],
    canonical = routeConfig?.canonical || location.pathname,
    ogImage = routeConfig?.ogImage,
    ogType = routeConfig?.ogType || 'website',
    twitterCard = routeConfig?.twitterCard || 'summary_large_image',
    twitterSite = routeConfig?.twitterSite || seoConfig.twitterHandle,
    noindex = routeConfig?.noindex || false,
    nofollow = routeConfig?.nofollow || false,
    structuredData = routeConfig?.structuredData,
  } = props;

  // Build final values
  const fullTitle = buildFullTitle(title);
  const canonicalUrl = buildCanonicalUrl(canonical);
  const ogImageUrl = buildOGImageUrl(ogImage);

  // Robots meta tag
  const robotsContent: string[] = [];
  if (noindex) robotsContent.push('noindex');
  if (nofollow) robotsContent.push('nofollow');
  const robotsTag = robotsContent.length > 0 ? robotsContent.join(', ') : 'index, follow';

  return (
    <>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>{fullTitle}</title>
        <meta name="title" content={fullTitle} />
        <meta name="description" content={description} />
        {keywords.length > 0 && <meta name="keywords" content={keywords.join(', ')} />}

        {/* Robots */}
        <meta name="robots" content={robotsTag} />

        {/* Language */}
        <html lang={seoConfig.language} />
        <meta name="language" content={seoConfig.language} />

        {/* Canonical URL */}
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content={ogType} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content={seoConfig.siteName} />
        <meta property="og:locale" content={seoConfig.locale} />

        {/* Twitter */}
        <meta name="twitter:card" content={twitterCard} />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImageUrl} />
        {twitterSite && <meta name="twitter:site" content={twitterSite} />}

        {/* Additional Meta Tags */}
        <meta name="author" content={seoConfig.organizationName} />
        <meta name="copyright" content={`© ${new Date().getFullYear()} ${seoConfig.organizationName}`} />
      </Helmet>

      {/* Structured Data */}
      {structuredData && <StructuredDataComponent data={structuredData} />}
    </>
  );
}
